import { 
  LicensePoolPublic, 
  Permission, 
  SubjectType,
} from '@pollination-solutions/pollination-sdk'
import { notification } from 'antd'
import { useAuth } from 'auth-context'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { OrganizationMemberWithRoles } from './useOrganizationMembers'


export interface UserPermission {
  user: OrganizationMemberWithRoles,
  permission: Permission
}

interface useLicenseUsersProps {
  initialLicense: LicensePoolPublic
  membersWithRoles: OrganizationMemberWithRoles[]
}

const useLicenseUsers = ({ initialLicense, membersWithRoles }: useLicenseUsersProps) => {
  const { client } = useAuth()

  const [license, setLicense] = useState<LicensePoolPublic>(initialLicense)

  useEffect(() => {
    setLicense(initialLicense)
  }, [initialLicense])

  const userPermissions = useMemo(() => {
    const ups = license.accessors?.map((accessor) => {
      const findUser = membersWithRoles.find(t => t.user.username === accessor.subject.name)
      if (!findUser) {
        return
      }
      return {
        user: findUser,
        permission: accessor.permission
      }
    }) ?? []

    return ups.filter(up => up !== undefined) as UserPermission[]
  }, [license, membersWithRoles])  

  const upsertUsersPermission = useCallback(async (users: OrganizationMemberWithRoles[], permission: Permission) => {
    const newAccessPolicies = users.map(user => ({
      permission,
      subject: {
        name: user.user.username,
        account_type: 'user',
        id: user.user.username,
        subject_type: SubjectType.User
      }
    }))

    return client.licenses.grantAccessToPool({ 
      poolId: license.id, 
      licensePoolAccessPolicyList: { resources: newAccessPolicies } })
      .then(({ data }) => {
        setLicense(data)
      })
      .catch((error) => {
        console.log(error)
      })
  }, [client, license])

  const removeUserPermission = useCallback(async (user: OrganizationMemberWithRoles) => {

    const accessorIndex = license.accessors?.findIndex(a => a.subject.id === user.user.username)
    if (!accessorIndex) return

    const accessPolicy = {
      name: user.user.username,
      subject_type: SubjectType.User,
    }

    return client.licenses.revokeAccessToPool({ 
      poolId: license.id, licensePoolPolicySubjectList: 
      { resources: [accessPolicy] } })
      .then(({ data }) => {
        setLicense(data)
      })
      .catch((error) => {
        notification.error({
          message: 'Error removing user from license pool.'
        })
      })
  }, [client, license])

  return {
    license,
    userPermissions,
    upsertUsersPermission,
    removeUserPermission
  }
}

export default useLicenseUsers